/**
 * Tema claro, oscuro o automático (arquitectura 2.16). En automático: si el sistema está en modo
 * oscuro se respeta; si no (casi todos reportan «claro» por omisión), manda la hora local:
 * oscuro de 19:00 a 7:00. La elección se guarda solo en ese navegador.
 */
export type Preferencia = 'claro' | 'oscuro' | 'auto';
export type Tema = 'claro' | 'oscuro';

export const CLAVE_TEMA = 'siniestrados.tema';
export const HORA_OSCURO = 19;
export const HORA_CLARO = 7;

export function resolverTema(preferencia: Preferencia, sistemaOscuro: boolean, hora: number): Tema {
  if (preferencia !== 'auto') return preferencia;
  if (sistemaOscuro) return 'oscuro';
  return hora >= HORA_OSCURO || hora < HORA_CLARO ? 'oscuro' : 'claro';
}

export function leerPreferencia(): Preferencia {
  try {
    const v = localStorage.getItem(CLAVE_TEMA);
    return v === 'claro' || v === 'oscuro' ? v : 'auto';
  } catch {
    return 'auto';
  }
}

export function guardarPreferencia(p: Preferencia) {
  try {
    if (p === 'auto') localStorage.removeItem(CLAVE_TEMA);
    else localStorage.setItem(CLAVE_TEMA, p);
  } catch {
    // Navegación privada o almacenamiento bloqueado: el tema vale solo en esta visita.
  }
}

const sistemaOscuro = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;

/** Aplica el tema a <html> y a la barra del navegador en el teléfono. */
export function aplicarTema(
  preferencia: Preferencia = leerPreferencia(),
  ahora = new Date(),
): Tema {
  const tema = resolverTema(preferencia, sistemaOscuro(), ahora.getHours());
  const raiz = document.documentElement;
  raiz.dataset['tema'] = tema;
  raiz.dataset['preferencia'] = preferencia;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', tema === 'oscuro' ? '#0a0a0b' : '#ffffff');
  return tema;
}

/**
 * Mantiene el tema al día: cambio del modo del sistema, paso de la hora (revisa cada minuto) y
 * cambios hechos en otra pestaña. Devuelve la función que lo detiene.
 */
export function vigilarTema(alCambiar?: (tema: Tema, preferencia: Preferencia) => void) {
  const actualizar = () => {
    const p = leerPreferencia();
    alCambiar?.(aplicarTema(p), p);
  };
  const consulta =
    typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
  consulta?.addEventListener('change', actualizar);
  const reloj = setInterval(actualizar, 60_000);
  const almacen = (e: StorageEvent) => e.key === CLAVE_TEMA && actualizar();
  addEventListener('storage', almacen);
  return () => {
    consulta?.removeEventListener('change', actualizar);
    clearInterval(reloj);
    removeEventListener('storage', almacen);
  };
}

/**
 * Script en línea para el <head> de las vitrinas (Next.js): fija el tema antes de pintar para que
 * no parpadee. Repite `resolverTema` sin dependencias; test/tema.test.ts comprueba que coinciden.
 */
export const SCRIPT_TEMA = `(function(){try{var p=localStorage.getItem('${CLAVE_TEMA}');}catch(e){}if(p!=='claro'&&p!=='oscuro')p='auto';var o=window.matchMedia&&matchMedia('(prefers-color-scheme: dark)').matches,h=new Date().getHours(),t=p!=='auto'?p:(o||h>=${HORA_OSCURO}||h<${HORA_CLARO})?'oscuro':'claro',r=document.documentElement;r.dataset.tema=t;r.dataset.preferencia=p;})();`;
